import { useEffect, useState } from 'react';
import { Navigation } from '@/components/Navigation';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { formatPrice } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, TrendingUp, Users, DollarSign, ChevronDown, ChevronUp, Settings, Key, Eye, EyeOff, FileText, Layout, Star, Check, X, MessageSquare, Instagram, Image, RefreshCw, Trash2, Edit, Upload, ImagePlus, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminDashboard() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [showSiteSettings, setShowSiteSettings] = useState(false);
  const [defaultHomepage, setDefaultHomepage] = useState('premium');
  const [savingSiteSettings, setSavingSiteSettings] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpEmailHint, setOtpEmailHint] = useState('');
  const [passwordData, setPasswordData] = useState({
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [sendingOtp, setSendingOtp] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    pendingOrders: 0
  });
  const [reviews, setReviews] = useState([]);
  const [showReviews, setShowReviews] = useState(false);
  const [loadingReviews, setLoadingReviews] = useState(false);
  
  // Products Management State
  const [products, setProducts] = useState([]);
  const [showProducts, setShowProducts] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({});
  const [savingProduct, setSavingProduct] = useState(false);
  const [showFullEditor, setShowFullEditor] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState(null); // -1 for main, index for gallery
  const [isDragging, setIsDragging] = useState(false);
  
  // Social Media State
  const [socialContent, setSocialContent] = useState([]);
  const [showSocialMedia, setShowSocialMedia] = useState(false);
  const [loadingSocial, setLoadingSocial] = useState(false);
  const [generatingSocial, setGeneratingSocial] = useState(false);

  useEffect(() => {
    // Wait for auth to finish loading before checking user
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }
    if (!user.is_admin) {
      navigate('/');
      toast.error('Admin access required');
      return;
    }
    fetchOrders();
    fetchSiteSettings();
    fetchReviews();
    fetchSocialContent();
    fetchProducts();
  }, [user, authLoading, navigate]);

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await axios.get(`${API_URL}/admin/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name || '',
      description: product.description || '',
      price: product.price || '',
      mrp: product.mrp || '',
      category: product.category || '',
      subcategory: product.subcategory || '',
      dimensions: product.dimensions || '',
      image_url: product.image_url || '',
      image_urls: product.image_urls || [],
      specifications: product.specifications || {},
      whats_included: product.whats_included || ''
    });
    setShowFullEditor(false);
  };

  const handleSaveProduct = async () => {
    if (!editingProduct) return;
    setSavingProduct(true);
    try {
      const updateData = {};
      
      // Basic fields
      if (productForm.name !== editingProduct.name) updateData.name = productForm.name;
      if (productForm.description !== editingProduct.description) updateData.description = productForm.description;
      if (productForm.price && parseFloat(productForm.price) !== editingProduct.price) updateData.price = parseFloat(productForm.price);
      if (productForm.mrp && parseFloat(productForm.mrp) !== editingProduct.mrp) updateData.mrp = parseFloat(productForm.mrp);
      if (productForm.category !== editingProduct.category) updateData.category = productForm.category;
      if (productForm.subcategory !== editingProduct.subcategory) updateData.subcategory = productForm.subcategory;
      if (productForm.dimensions !== editingProduct.dimensions) updateData.dimensions = productForm.dimensions;
      if (productForm.image_url !== editingProduct.image_url) updateData.image_url = productForm.image_url;
      
      // Image gallery
      if (JSON.stringify(productForm.image_urls) !== JSON.stringify(editingProduct.image_urls || [])) {
        updateData.image_urls = productForm.image_urls;
      }
      
      // Specifications - always include if editing full specs
      if (showFullEditor && JSON.stringify(productForm.specifications) !== JSON.stringify(editingProduct.specifications || {})) {
        updateData.specifications = productForm.specifications;
      }
      
      // What's Included
      if (productForm.whats_included !== (editingProduct.whats_included || '')) {
        updateData.whats_included = productForm.whats_included;
      }
      
      if (Object.keys(updateData).length === 0) {
        toast.info('No changes to save');
        setEditingProduct(null);
        setShowFullEditor(false);
        return;
      }
      
      await axios.put(`${API_URL}/admin/products/${editingProduct.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product updated successfully!');
      setEditingProduct(null);
      setShowFullEditor(false);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update product');
      console.error('Error updating product:', error);
    } finally {
      setSavingProduct(false);
    }
  };

  // Update specification field
  const updateSpecField = (path, value) => {
    setProductForm(prev => {
      const newSpecs = { ...prev.specifications };
      const keys = path.split('.');
      let current = newSpecs;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return { ...prev, specifications: newSpecs };
    });
  };

  // Add image to gallery
  const addImageToGallery = (url) => {
    if (url && !productForm.image_urls.includes(url)) {
      setProductForm(prev => ({
        ...prev,
        image_urls: [...prev.image_urls, url]
      }));
    }
  };

  // Remove image from gallery
  const removeImageFromGallery = (index) => {
    setProductForm(prev => ({
      ...prev,
      image_urls: prev.image_urls.filter((_, i) => i !== index)
    }));
  };

  // Replace image in gallery at specific index
  const replaceGalleryImage = (index, newUrl) => {
    if (newUrl) {
      setProductForm(prev => ({
        ...prev,
        image_urls: prev.image_urls.map((url, i) => i === index ? newUrl : url)
      }));
    }
  };

  // Upload image file with auto-optimization/compression
  const uploadProductImage = async (file, targetIndex = null) => {
    // targetIndex: -1 = main image, null = add to gallery, number = replace gallery index
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, or WebP images.');
      return;
    }
    
    setUploadingImage(true);
    setUploadingIndex(targetIndex);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/admin/upload-product-image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        // Construct full URL
        const baseUrl = process.env.REACT_APP_BACKEND_URL;
        const imageUrl = baseUrl + response.data.url;
        
        // Show optimization/compression message
        if (response.data.optimized || response.data.compressed) {
          const savedKB = Math.round((response.data.original_size - response.data.final_size) / 1024);
          toast.success(`${response.data.message} (saved ${savedKB}KB)`);
        } else {
          toast.success('Image uploaded successfully!');
        }
        
        // Update appropriate field
        if (targetIndex === -1) {
          // Main image
          setProductForm(prev => ({ ...prev, image_url: imageUrl }));
        } else if (targetIndex === null) {
          // Add to gallery
          setProductForm(prev => ({
            ...prev,
            image_urls: [...(prev.image_urls || []), imageUrl]
          }));
        } else {
          // Replace specific gallery image
          replaceGalleryImage(targetIndex, imageUrl);
        }
      } else {
        toast.error(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
      setUploadingIndex(null);
    }
  };

  // Handle file input change
  const handleFileSelect = (e, targetIndex) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadProductImage(file, targetIndex);
    }
    // Reset input
    e.target.value = '';
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      // Upload first file to gallery
      uploadProductImage(files[0], null);
    }
  };

  // Handle paste in input
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        // Pasted an image file
        const file = items[i].getAsFile();
        if (file) {
          e.preventDefault();
          uploadProductImage(file, null);
          return;
        }
      }
    }
    // If no image file, let the URL paste through normally
  };

  const fetchSocialContent = async () => {
    setLoadingSocial(true);
    try {
      const response = await axios.get(`${API_URL}/admin/social-media/content?page_size=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSocialContent(response.data.content || []);
    } catch (error) {
      console.error('Error fetching social content:', error);
    } finally {
      setLoadingSocial(false);
    }
  };

  const generateSocialContent = async () => {
    setGeneratingSocial(true);
    try {
      await axios.post(`${API_URL}/admin/social-media/generate-daily-content`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Content generated!');
      fetchSocialContent();
    } catch (error) {
      toast.error('Failed to generate content');
    } finally {
      setGeneratingSocial(false);
    }
  };

  const approveSocialContent = async (contentId) => {
    try {
      await axios.post(`${API_URL}/admin/social-media/content/${contentId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Content approved!');
      fetchSocialContent();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const rejectSocialContent = async (contentId) => {
    try {
      await axios.post(`${API_URL}/admin/social-media/content/${contentId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Content rejected & deleted');
      fetchSocialContent();
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  const fetchReviews = async () => {
    setLoadingReviews(true);
    try {
      const response = await axios.get(`${API_URL}/admin/reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoadingReviews(false);
    }
  };

  const approveReview = async (reviewId) => {
    try {
      await axios.put(`${API_URL}/admin/reviews/${reviewId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review approved!');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to approve review');
    }
  };

  const deleteReview = async (reviewId) => {
    try {
      await axios.delete(`${API_URL}/admin/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review deleted');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/site-settings`);
      setDefaultHomepage(response.data.default_homepage || 'premium');
    } catch (error) {
      console.error('Error fetching site settings:', error);
    }
  };

  const saveSiteSettings = async () => {
    setSavingSiteSettings(true);
    try {
      await axios.put(
        `${API_URL}/admin/site-settings`,
        { default_homepage: defaultHomepage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Default homepage set to ${defaultHomepage === 'premium' ? 'Premium' : 'Classic'}`);
      setShowSiteSettings(false);
    } catch (error) {
      console.error('Error saving site settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSavingSiteSettings(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle paginated response - extract orders array
      const ordersData = response.data.orders || response.data;
      setOrders(Array.isArray(ordersData) ? ordersData : []);

      // Calculate stats from the orders array
      const ordersArray = Array.isArray(ordersData) ? ordersData : [];
      const totalRevenue = ordersArray.reduce((sum, order) => sum + order.total, 0);
      const pendingOrders = ordersArray.filter(order => order.status === 'pending').length;
      setStats({
        totalOrders: response.data.total || ordersArray.length,
        totalRevenue,
        pendingOrders
      });
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(
        `${API_URL}/admin/orders/${orderId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order status');
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to permanently delete this cancelled order?')) {
      return;
    }
    try {
      await axios.delete(
        `${API_URL}/admin/orders/${orderId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order deleted successfully');
      fetchOrders();
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    }
  };

  const toggleOrderExpand = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'shipped':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (!passwordData.otp || passwordData.otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(
        `${API_URL}/auth/admin/verify-password-otp`,
        {
          otp: passwordData.otp,
          new_password: passwordData.newPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password changed successfully!');
      setPasswordData({ otp: '', newPassword: '', confirmPassword: '' });
      setShowSettings(false);
      setOtpSent(false);
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSendOtp = async () => {
    setSendingOtp(true);
    try {
      const response = await axios.post(
        `${API_URL}/auth/admin/send-password-otp`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOtpSent(true);
      setOtpEmailHint(response.data.email_hint);
      toast.success('OTP sent to your registered email!');
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  // Show loading while auth is being verified
  if (authLoading) {
    return (
      <div className="min-h-screen noise-texture flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground uppercase tracking-wider">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!user || !user.is_admin) {
    return null;
  }

  return (
    <div className="min-h-screen noise-texture">
      <Navigation />

      <div className="container mx-auto px-8 py-12">
        <div className="flex items-center justify-between mb-12">
          <h1 className="text-5xl md:text-6xl font-black uppercase">ADMIN DASHBOARD</h1>
          <div className="flex gap-3">
            <Button 
              onClick={() => setShowSiteSettings(true)}
              variant="outline"
              className="rounded-none uppercase tracking-wider font-bold"
            >
              <Layout className="w-4 h-4 mr-2" />
              Site Settings
            </Button>
            <Button 
              onClick={() => setShowSettings(true)}
              variant="outline"
              className="rounded-none uppercase tracking-wider font-bold"
            >
              <Settings className="w-4 h-4 mr-2" />
              Password
            </Button>
            <Link to="/admin/buyers">
              <Button className="bg-blue-600 text-white hover:bg-blue-700 rounded-none uppercase tracking-wider font-bold">
                <Users className="w-4 h-4 mr-2" />
                Buyers List
              </Button>
            </Link>
            <Link to="/admin/leads">
              <Button className="bg-orange-600 text-white hover:bg-orange-700 rounded-none uppercase tracking-wider font-bold">
                <TrendingUp className="w-4 h-4 mr-2" />
                Leads KPI
              </Button>
            </Link>
            <Link to="/admin/blog">
              <Button className="bg-green-600 text-white hover:bg-green-700 rounded-none uppercase tracking-wider font-bold">
                <FileText className="w-4 h-4 mr-2" />
                Blog
              </Button>
            </Link>
            <Button 
              onClick={() => setShowProducts(!showProducts)}
              className={`${showProducts ? 'bg-primary/90' : 'bg-primary'} text-primary-foreground hover:bg-primary/90 rounded-none uppercase tracking-wider font-bold`}
            >
              <Package className="w-4 h-4 mr-2" />
              Products ({products.length})
            </Button>
            <Button 
              onClick={() => setShowReviews(!showReviews)}
              className={`${showReviews ? 'bg-yellow-600' : 'bg-yellow-500'} text-white hover:bg-yellow-600 rounded-none uppercase tracking-wider font-bold`}
            >
              <Star className="w-4 h-4 mr-2" />
              Reviews ({reviews.filter(r => !r.is_approved).length})
            </Button>
            <Button 
              onClick={() => setShowSocialMedia(!showSocialMedia)}
              className={`${showSocialMedia ? 'bg-purple-700' : 'bg-purple-600'} text-white hover:bg-purple-700 rounded-none uppercase tracking-wider font-bold`}
            >
              <Instagram className="w-4 h-4 mr-2" />
              Social ({socialContent.filter(c => c.status === 'pending').length})
            </Button>
          </div>
        </div>

        {/* Social Media Content Section */}
        {showSocialMedia && (
          <div className="mb-8 border-2 border-purple-500 bg-purple-50 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold uppercase flex items-center gap-2">
                <Instagram className="w-6 h-6" />
                Social Media Content
              </h2>
              <div className="flex gap-2">
                <Button 
                  onClick={generateSocialContent}
                  disabled={generatingSocial}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {generatingSocial ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Image className="w-4 h-4 mr-2" />}
                  Generate Content
                </Button>
                <Button variant="ghost" onClick={() => setShowSocialMedia(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            {loadingSocial ? (
              <p className="text-gray-500">Loading content...</p>
            ) : socialContent.length === 0 ? (
              <p className="text-gray-500">No content yet. Click "Generate Content" to create posts.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                {socialContent.filter(c => c.status === 'pending').map((item) => (
                  <div key={item.id} className="p-4 border-2 border-purple-200 bg-white">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold uppercase bg-purple-100 px-2 py-1">{item.content_type}</span>
                      <span className="text-xs bg-yellow-100 px-2 py-1">PENDING</span>
                    </div>
                    {item.media_urls?.[0] && (
                      <img src={item.media_urls[0]} alt="" className="w-full h-24 object-cover mb-2" />
                    )}
                    <p className="text-xs text-gray-600 mb-2 line-clamp-3">{item.caption}</p>
                    <p className="text-xs text-gray-400 mb-2">{item.product_name}</p>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => approveSocialContent(item.id)} className="bg-green-600 hover:bg-green-700 flex-1">
                        <Check className="w-3 h-3 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => rejectSocialContent(item.id)} className="flex-1">
                        <X className="w-3 h-3 mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {socialContent.filter(c => c.status === 'pending').length === 0 && socialContent.length > 0 && (
              <p className="text-green-600 text-center py-4">All content approved! Generate more or check scheduled posts.</p>
            )}
          </div>
        )}

        {/* Reviews Management Section */}
        {showReviews && (
          <div className="mb-8 border-2 border-yellow-500 bg-yellow-50 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold uppercase flex items-center gap-2">
                <MessageSquare className="w-6 h-6" />
                Product Reviews
              </h2>
              <Button variant="ghost" onClick={() => setShowReviews(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {loadingReviews ? (
              <p className="text-gray-500">Loading reviews...</p>
            ) : reviews.length === 0 ? (
              <p className="text-gray-500">No reviews yet</p>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div 
                    key={review.id} 
                    className={`p-4 border-2 ${review.is_approved ? 'border-green-300 bg-green-50' : 'border-yellow-300 bg-white'}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-bold">{review.reviewer_name}</span>
                          <span className="text-gray-500">•</span>
                          <span className="text-gray-600">{review.company_name}</span>
                          <span className={`px-2 py-0.5 text-xs font-bold ${review.is_approved ? 'bg-green-200 text-green-800' : 'bg-yellow-200 text-yellow-800'}`}>
                            {review.is_approved ? 'APPROVED' : 'PENDING'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i} 
                              className={`w-4 h-4 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
                            />
                          ))}
                        </div>
                        <p className="font-semibold text-gray-800 mb-1">{review.title}</p>
                        <p className="text-gray-600 text-sm mb-2">{review.review_text}</p>
                        <p className="text-xs text-gray-400">Product: {review.product_name} • {new Date(review.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        {!review.is_approved && (
                          <Button 
                            size="sm" 
                            onClick={() => approveReview(review.id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => deleteReview(review.id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Products Management Section */}
        {showProducts && (
          <div className="mb-8 border-2 border-primary bg-gray-50 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold uppercase flex items-center gap-2">
                <Package className="w-6 h-6" />
                Product Management
              </h2>
              <Button variant="ghost" onClick={() => setShowProducts(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {loadingProducts ? (
              <p className="text-gray-500">Loading products...</p>
            ) : products.length === 0 ? (
              <p className="text-gray-500">No products found</p>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {products.map((product) => (
                  <div 
                    key={product.id} 
                    className="p-4 border-2 border-gray-200 bg-white hover:border-primary/50 transition-colors"
                  >
                    {editingProduct?.id === product.id ? (
                      // Edit Mode - Comprehensive Editor
                      <div className="space-y-4">
                        {/* Basic Info */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="col-span-2">
                            <Label className="text-xs font-bold uppercase">Name</Label>
                            <Input
                              value={productForm.name}
                              onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                              className="mt-1"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs font-bold uppercase">Description</Label>
                            <textarea
                              value={productForm.description}
                              onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                              className="mt-1 w-full p-2 border rounded text-sm min-h-[60px]"
                            />
                          </div>
                          <div>
                            <Label className="text-xs font-bold uppercase">Price (₹)</Label>
                            <Input
                              type="number"
                              value={productForm.price}
                              onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs font-bold uppercase">MRP (₹)</Label>
                            <Input
                              type="number"
                              value={productForm.mrp}
                              onChange={(e) => setProductForm({...productForm, mrp: e.target.value})}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs font-bold uppercase">Category</Label>
                            <Input
                              value={productForm.category}
                              onChange={(e) => setProductForm({...productForm, category: e.target.value})}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs font-bold uppercase">Subcategory</Label>
                            <Input
                              value={productForm.subcategory}
                              onChange={(e) => setProductForm({...productForm, subcategory: e.target.value})}
                              className="mt-1"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs font-bold uppercase">Dimensions</Label>
                            <Input
                              value={productForm.dimensions}
                              onChange={(e) => setProductForm({...productForm, dimensions: e.target.value})}
                              className="mt-1"
                              placeholder="e.g., 2100mm x 550mm"
                            />
                          </div>
                        </div>

                        {/* Toggle Full Editor */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowFullEditor(!showFullEditor)}
                          className="w-full"
                        >
                          {showFullEditor ? '▲ Hide Specifications & Gallery' : '▼ Edit Specifications & Gallery'}
                        </Button>

                        {/* Full Specifications Editor */}
                        {showFullEditor && (
                          <div className="border-t pt-4 space-y-4">
                            {/* Main Image */}
                            <div>
                              <Label className="text-xs font-bold uppercase">Main Image</Label>
                              <div className="flex gap-2 mt-1">
                                <Input
                                  value={productForm.image_url}
                                  onChange={(e) => setProductForm({...productForm, image_url: e.target.value})}
                                  className="flex-1"
                                  placeholder="https://... or browse to upload"
                                />
                                <input
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp"
                                  onChange={(e) => handleFileSelect(e, -1)}
                                  className="hidden"
                                  id={`main-image-upload-${product.id}`}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => document.getElementById(`main-image-upload-${product.id}`)?.click()}
                                  disabled={uploadingImage && uploadingIndex === -1}
                                  className="flex items-center gap-1"
                                  title="Upload image from computer"
                                >
                                  {uploadingImage && uploadingIndex === -1 ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Upload className="w-3 h-3" />
                                  )}
                                  Browse
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    const newUrl = window.prompt('Enter new main image URL:', productForm.image_url);
                                    if (newUrl && newUrl.trim()) {
                                      setProductForm({...productForm, image_url: newUrl.trim()});
                                    }
                                  }}
                                  className="flex items-center gap-1"
                                  title="Replace with URL"
                                >
                                  <RefreshCw className="w-3 h-3" />
                                </Button>
                              </div>
                              {productForm.image_url && (
                                <img src={productForm.image_url} alt="Preview" className="mt-2 h-20 object-contain border" />
                              )}
                            </div>

                            {/* Image Gallery */}
                            <div>
                              <Label className="text-xs font-bold uppercase">Image Gallery ({(productForm.image_urls || []).length} images)</Label>
                              
                              {/* Existing gallery images */}
                              <div className="space-y-2 mt-2">
                                {(productForm.image_urls || []).map((url, idx) => (
                                  <div key={idx} className="flex items-center gap-2 p-2 border bg-gray-50 rounded">
                                    <img src={url} alt={`Gallery ${idx + 1}`} className="h-12 w-12 object-cover border flex-shrink-0 rounded" />
                                    <Input
                                      value={url}
                                      onChange={(e) => replaceGalleryImage(idx, e.target.value)}
                                      className="flex-1 text-xs"
                                      placeholder="Image URL..."
                                    />
                                    <input
                                      type="file"
                                      accept="image/jpeg,image/png,image/webp"
                                      onChange={(e) => handleFileSelect(e, idx)}
                                      className="hidden"
                                      id={`gallery-upload-${product.id}-${idx}`}
                                    />
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => document.getElementById(`gallery-upload-${product.id}-${idx}`)?.click()}
                                      disabled={uploadingImage && uploadingIndex === idx}
                                      title="Upload replacement image"
                                    >
                                      {uploadingImage && uploadingIndex === idx ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <Upload className="w-3 h-3" />
                                      )}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        const newUrl = window.prompt('Enter new image URL:', url);
                                        if (newUrl && newUrl.trim()) {
                                          replaceGalleryImage(idx, newUrl.trim());
                                        }
                                      }}
                                      title="Replace with URL"
                                    >
                                      <RefreshCw className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => {
                                        if (window.confirm('Delete this image from gallery?')) {
                                          removeImageFromGallery(idx);
                                        }
                                      }}
                                      title="Delete this image"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                              
                              {/* Drag & Drop Zone + Add New */}
                              <div 
                                className={`mt-3 border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                                  isDragging ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400'
                                }`}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                              >
                                <div className="flex flex-col items-center gap-2">
                                  <ImagePlus className={`w-8 h-8 ${isDragging ? 'text-primary' : 'text-gray-400'}`} />
                                  <p className="text-sm text-gray-600">
                                    {isDragging ? 'Drop image here!' : 'Drag & drop images here'}
                                  </p>
                                  <p className="text-xs text-gray-400">or</p>
                                  <div className="flex gap-2">
                                    <input
                                      type="file"
                                      accept="image/jpeg,image/png,image/webp"
                                      onChange={(e) => handleFileSelect(e, null)}
                                      className="hidden"
                                      id={`gallery-add-upload-${product.id}`}
                                    />
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => document.getElementById(`gallery-add-upload-${product.id}`)?.click()}
                                      disabled={uploadingImage && uploadingIndex === null}
                                      className="flex items-center gap-1"
                                    >
                                      {uploadingImage && uploadingIndex === null ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <Upload className="w-3 h-3" />
                                      )}
                                      Browse Files
                                    </Button>
                                  </div>
                                </div>
                                
                                {/* URL paste input */}
                                <div className="flex gap-2 mt-3">
                                  <Input
                                    placeholder="Paste image URL here"
                                    id={`gallery-input-${product.id}`}
                                    className="flex-1 text-sm"
                                    onPaste={handlePaste}
                                  />
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      const input = document.getElementById(`gallery-input-${product.id}`);
                                      if (input?.value) {
                                        addImageToGallery(input.value);
                                        input.value = '';
                                      }
                                    }}
                                  >
                                    + Add URL
                                  </Button>
                                </div>
                                <p className="text-xs text-gray-400 mt-2">
                                  Supports JPG, PNG, WebP • Auto-optimizes large images
                                </p>
                              </div>
                            </div>

                            {/* Specifications */}
                            <div className="space-y-3">
                              <Label className="text-xs font-bold uppercase text-primary">Product Specifications</Label>
                              
                              <div>
                                <Label className="text-xs text-gray-600">Color Variant</Label>
                                <Input
                                  value={productForm.specifications?.color_variant || ''}
                                  onChange={(e) => updateSpecField('color_variant', e.target.value)}
                                  className="mt-1"
                                  placeholder="e.g., Walnut + Yellow"
                                />
                              </div>

                              <div>
                                <Label className="text-xs text-gray-600">Material</Label>
                                <textarea
                                  value={productForm.specifications?.material || ''}
                                  onChange={(e) => updateSpecField('material', e.target.value)}
                                  className="mt-1 w-full p-2 border rounded text-sm min-h-[60px]"
                                  placeholder="Material description..."
                                />
                              </div>

                              <div>
                                <Label className="text-xs text-gray-600">Style</Label>
                                <Input
                                  value={productForm.specifications?.style || ''}
                                  onChange={(e) => updateSpecField('style', e.target.value)}
                                  className="mt-1"
                                  placeholder="e.g., Corporate, Modern"
                                />
                              </div>

                              <div>
                                <Label className="text-xs text-gray-600">Installation</Label>
                                <Input
                                  value={productForm.specifications?.installation || ''}
                                  onChange={(e) => updateSpecField('installation', e.target.value)}
                                  className="mt-1"
                                  placeholder="e.g., Self Assembly"
                                />
                              </div>

                              {/* Detailed Dimensions */}
                              <div>
                                <Label className="text-xs text-gray-600 font-bold">Detailed Dimensions</Label>
                                <div className="grid grid-cols-2 gap-2 mt-1">
                                  <Input
                                    placeholder="Width"
                                    value={productForm.specifications?.detailed_dimensions?.width || ''}
                                    onChange={(e) => updateSpecField('detailed_dimensions.width', e.target.value)}
                                  />
                                  <Input
                                    placeholder="Depth"
                                    value={productForm.specifications?.detailed_dimensions?.depth || ''}
                                    onChange={(e) => updateSpecField('detailed_dimensions.depth', e.target.value)}
                                  />
                                  <Input
                                    placeholder="Height"
                                    value={productForm.specifications?.detailed_dimensions?.height || ''}
                                    onChange={(e) => updateSpecField('detailed_dimensions.height', e.target.value)}
                                  />
                                  <Input
                                    placeholder="Top Thickness"
                                    value={productForm.specifications?.detailed_dimensions?.top_thickness || ''}
                                    onChange={(e) => updateSpecField('detailed_dimensions.top_thickness', e.target.value)}
                                  />
                                </div>
                              </div>

                              {/* Colors */}
                              <div>
                                <Label className="text-xs text-gray-600 font-bold">Colors</Label>
                                <div className="grid grid-cols-3 gap-2 mt-1">
                                  <Input
                                    placeholder="Top Color"
                                    value={productForm.specifications?.colors?.top || ''}
                                    onChange={(e) => updateSpecField('colors.top', e.target.value)}
                                  />
                                  <Input
                                    placeholder="Glass/Screen"
                                    value={productForm.specifications?.colors?.glass_screen || ''}
                                    onChange={(e) => updateSpecField('colors.glass_screen', e.target.value)}
                                  />
                                  <Input
                                    placeholder="Base Frame"
                                    value={productForm.specifications?.colors?.base_frame || ''}
                                    onChange={(e) => updateSpecField('colors.base_frame', e.target.value)}
                                  />
                                </div>
                              </div>

                              {/* Features */}
                              <div>
                                <Label className="text-xs text-gray-600 font-bold">Features (one per line)</Label>
                                <textarea
                                  value={(productForm.specifications?.features || []).join('\n')}
                                  onChange={(e) => updateSpecField('features', e.target.value.split('\n').filter(f => f.trim()))}
                                  className="mt-1 w-full p-2 border rounded text-sm min-h-[100px]"
                                  placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                                />
                              </div>

                              {/* Certifications */}
                              <div>
                                <Label className="text-xs text-gray-600 font-bold">Certifications (comma separated)</Label>
                                <Input
                                  value={(productForm.specifications?.certifications || []).join(', ')}
                                  onChange={(e) => updateSpecField('certifications', e.target.value.split(',').map(c => c.trim()).filter(c => c))}
                                  className="mt-1"
                                  placeholder="ISO 9001:2015, IGBC Certified"
                                />
                              </div>
                            </div>

                            {/* What's Included */}
                            <div className="border-t pt-4">
                              <Label className="text-xs font-bold uppercase text-primary">What's Included</Label>
                              <p className="text-xs text-gray-500 mb-2">Describe what comes with this product (packaging, accessories, etc.)</p>
                              <textarea
                                value={productForm.whats_included || ''}
                                onChange={(e) => setProductForm({...productForm, whats_included: e.target.value})}
                                className="w-full p-2 border rounded text-sm min-h-[80px]"
                                placeholder="e.g., 1x Workstation desk, 1x Monitor stand, Assembly hardware kit, Installation guide..."
                              />
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-2 pt-2 border-t">
                          <Button 
                            size="sm" 
                            onClick={handleSaveProduct}
                            disabled={savingProduct}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {savingProduct ? 'Saving...' : 'Save All Changes'}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => { setEditingProduct(null); setShowFullEditor(false); }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <div className="flex gap-4">
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-16 h-16 object-cover border"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-sm truncate">{product.name}</p>
                          <p className="text-xs text-gray-500 truncate">{product.description}</p>
                          <p className="text-sm font-bold text-green-700 mt-1">
                            {formatPrice(product.price)}
                            {product.mrp && product.mrp > product.price && (
                              <span className="text-gray-400 line-through ml-2 font-normal">
                                {formatPrice(product.mrp)}
                              </span>
                            )}
                          </p>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleEditProduct(product)}
                        >
                          Edit
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Settings Modal */}
        {showSettings && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white border-2 border-primary rounded-none p-8 max-w-md w-full mx-4">
              <div className="flex items-center gap-3 mb-6">
                <Key className="w-6 h-6 text-primary" />
                <h2 className="text-2xl font-black uppercase">Change Password</h2>
              </div>
              
              {!otpSent ? (
                // Step 1: Request OTP
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-none">
                    <p className="text-sm text-blue-800">
                      For security, an OTP will be sent to your registered admin email to verify your identity.
                    </p>
                  </div>
                  
                  <Button
                    onClick={handleSendOtp}
                    disabled={sendingOtp}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase tracking-wider font-bold py-4"
                  >
                    {sendingOtp ? 'Sending OTP...' : 'Send OTP to Email'}
                  </Button>
                  
                  <Button
                    onClick={() => setShowSettings(false)}
                    variant="outline"
                    className="w-full rounded-none uppercase tracking-wider font-bold"
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                // Step 2: Enter OTP and New Password
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div className="bg-green-50 border border-green-200 p-4 rounded-none">
                    <p className="text-sm text-green-800">
                      ✅ OTP sent to <strong>{otpEmailHint}</strong>
                    </p>
                    <p className="text-xs text-green-600 mt-1">Valid for 10 minutes</p>
                  </div>
                  
                  <div>
                    <Label className="uppercase tracking-wider text-xs font-bold">Enter OTP</Label>
                    <Input
                      type="text"
                      value={passwordData.otp}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, otp: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                      required
                      maxLength={6}
                      className="rounded-none text-center text-2xl tracking-widest font-mono"
                      placeholder="000000"
                    />
                  </div>
                  
                  <div>
                    <Label className="uppercase tracking-wider text-xs font-bold">New Password</Label>
                    <div className="relative">
                      <Input
                        type={showNewPassword ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                        required
                        minLength={8}
                        className="rounded-none pr-10"
                        placeholder="Min 8 characters"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="uppercase tracking-wider text-xs font-bold">Confirm New Password</Label>
                    <Input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      required
                      className="rounded-none"
                      placeholder="Re-enter new password"
                    />
                  </div>
                  
                  <div className="bg-accent/20 p-3 rounded-none text-xs">
                    <p className="font-bold mb-1">Password Requirements:</p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1">
                      <li>At least 8 characters</li>
                      <li>Mix of letters, numbers & symbols recommended</li>
                    </ul>
                  </div>
                  
                  <div className="flex gap-4 pt-4">
                    <Button
                      type="submit"
                      disabled={changingPassword}
                      className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase tracking-wider font-bold"
                    >
                      {changingPassword ? 'Changing...' : 'Change Password'}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        setShowSettings(false);
                        setOtpSent(false);
                        setPasswordData({ otp: '', newPassword: '', confirmPassword: '' });
                      }}
                      variant="outline"
                      className="rounded-none uppercase tracking-wider font-bold"
                    >
                      Cancel
                    </Button>
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => {
                      setOtpSent(false);
                      handleSendOtp();
                    }}
                    className="w-full text-sm text-primary hover:underline"
                  >
                    Didn&apos;t receive OTP? Resend
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Site Settings Modal */}
        {showSiteSettings && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white border-2 border-primary rounded-none p-8 max-w-md w-full mx-4">
              <div className="flex items-center gap-3 mb-6">
                <Layout className="w-6 h-6 text-primary" />
                <h2 className="text-2xl font-black uppercase">Site Settings</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <Label className="uppercase tracking-wider text-xs font-bold mb-3 block">Default Homepage Theme</Label>
                  <p className="text-sm text-muted-foreground mb-4">
                    Choose which homepage design visitors see by default when they first visit the site.
                  </p>
                  
                  <div className="space-y-3">
                    <label className={`flex items-center gap-4 p-4 border-2 cursor-pointer transition-colors ${defaultHomepage === 'premium' ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'}`}>
                      <input
                        type="radio"
                        name="homepage"
                        value="premium"
                        checked={defaultHomepage === 'premium'}
                        onChange={(e) => setDefaultHomepage(e.target.value)}
                        className="w-4 h-4 text-primary"
                      />
                      <div>
                        <p className="font-bold">Premium Theme</p>
                        <p className="text-xs text-muted-foreground">Dark, sophisticated B2B design with gold accents</p>
                      </div>
                    </label>
                    
                    <label className={`flex items-center gap-4 p-4 border-2 cursor-pointer transition-colors ${defaultHomepage === 'classic' ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'}`}>
                      <input
                        type="radio"
                        name="homepage"
                        value="classic"
                        checked={defaultHomepage === 'classic'}
                        onChange={(e) => setDefaultHomepage(e.target.value)}
                        className="w-4 h-4 text-primary"
                      />
                      <div>
                        <p className="font-bold">Classic Theme</p>
                        <p className="text-xs text-muted-foreground">Light, friendly design with primary brand colors</p>
                      </div>
                    </label>
                  </div>
                  
                  <p className="text-xs text-muted-foreground mt-4">
                    Note: Visitors can still switch themes using the toggle in the footer. Their preference will be saved.
                  </p>
                </div>
                
                <div className="flex gap-4 pt-4">
                  <Button
                    onClick={saveSiteSettings}
                    disabled={savingSiteSettings}
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase tracking-wider font-bold"
                  >
                    {savingSiteSettings ? 'Saving...' : 'Save Settings'}
                  </Button>
                  <Button
                    onClick={() => setShowSiteSettings(false)}
                    variant="outline"
                    className="rounded-none uppercase tracking-wider font-bold"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12">
          <div className="bg-white border border-border rounded-none p-6">
            <div className="flex items-center justify-between mb-2">
              <Package className="w-8 h-8 text-accent" />
              <p className="mono text-3xl font-black" data-testid="stats-total-orders">{stats.totalOrders}</p>
            </div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">TOTAL ORDERS</p>
          </div>

          <div className="bg-white border border-border rounded-none p-6">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-accent" />
              <p className="mono text-2xl font-black" data-testid="stats-revenue">{formatPrice(stats.totalRevenue)}</p>
            </div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">TOTAL REVENUE</p>
          </div>

          <div className="bg-white border border-border rounded-none p-6">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-accent" />
              <p className="mono text-3xl font-black" data-testid="stats-pending">{stats.pendingOrders}</p>
            </div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">PENDING ORDERS</p>
          </div>

          <div className="bg-white border border-border rounded-none p-6">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8 text-accent" />
              <p className="mono text-2xl font-black">{formatPrice(stats.totalRevenue / Math.max(stats.totalOrders, 1))}</p>
            </div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">AVG ORDER VALUE</p>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white border border-border rounded-none p-8">
          <h2 className="text-2xl font-black uppercase mb-6">ALL ORDERS</h2>

          {loading ? (
            <p className="text-center py-12 text-muted-foreground">Loading orders...</p>
          ) : orders.length === 0 ? (
            <p className="text-center py-12 text-muted-foreground">No orders yet</p>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <div
                  key={order.id}
                  data-testid={`admin-order-${order.id}`}
                  className="border border-border rounded-none p-6 hover:border-accent transition-colors"
                >
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">ORDER ID</p>
                      <p className="mono text-sm font-bold">{order.id.substring(0, 8)}...</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">CUSTOMER</p>
                      <p className="text-sm font-bold">{order.user_name}</p>
                      <p className="mono text-xs text-muted-foreground">{order.user_email}</p>
                      {order.buyer_details?.contact_number && (
                        <p className="mono text-xs font-semibold text-primary mt-1">
                          📞 {order.buyer_details.contact_number}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">DATE</p>
                      <p className="text-sm">
                        {new Date(order.created_at).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">TOTAL</p>
                      <p className="mono font-black text-lg">{formatPrice(order.total)}</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">ITEMS</p>
                    <div className="space-y-1">
                      {order.items.map((item, index) => (
                        <p key={index} className="text-sm">
                          {item.product_name} x {item.quantity} - {formatPrice(item.total)}
                        </p>
                      ))}
                    </div>
                  </div>

                  {/* Delivery Timeframe */}
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-none">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">EXPECTED DELIVERY TIMEFRAME</p>
                    {(() => {
                      const days = order.subtotal <= 50000 ? 7 : 
                                   order.subtotal <= 100000 ? 10 : 
                                   order.subtotal <= 300000 ? 15 : 
                                   order.subtotal <= 500000 ? 30 : 45;
                      
                      const orderDate = new Date(order.created_at);
                      const deliveryDate = new Date(orderDate);
                      deliveryDate.setDate(deliveryDate.getDate() + days);
                      
                      return (
                        <>
                          <p className="text-lg font-black text-blue-600">
                            {days} days
                          </p>
                          <p className="text-sm font-bold text-blue-700 mt-1">
                            📅 Delivery by: {deliveryDate.toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Order placed: {orderDate.toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                        </>
                      );
                    })()}
                  </div>

                  {/* Expandable Buyer Details Section - Show for ALL orders */}
                  <div className="mb-4">
                    <button
                      onClick={() => toggleOrderExpand(order.id)}
                      className="flex items-center gap-2 text-primary hover:text-accent font-bold text-sm uppercase tracking-wider transition-colors"
                    >
                      {expandedOrders[order.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      {expandedOrders[order.id] ? 'HIDE' : 'SHOW'} BUYER DETAILS
                    </button>
                    
                    {expandedOrders[order.id] && (
                      <div className="mt-4 p-4 bg-accent/10 border border-accent rounded-none">
                        <h4 className="text-xs uppercase tracking-widest font-bold mb-3 text-accent-foreground">COMPLETE BUYER INFORMATION</h4>
                        
                        {order.buyer_details ? (
                          // New orders with complete buyer details
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Buyer Name</p>
                                <p className="font-semibold">{order.buyer_details.name}</p>
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Email</p>
                                <p className="mono text-sm">{order.buyer_details.email}</p>
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Contact Number</p>
                                <p className="mono font-semibold">{order.buyer_details.contact_number}</p>
                              </div>
                              {order.buyer_details.alternate_number && (
                                <div>
                                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Alternate Number</p>
                                  <p className="mono">{order.buyer_details.alternate_number}</p>
                                </div>
                              )}
                              {order.buyer_details.gst_number && (
                                <div>
                                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">GST Number</p>
                                  <p className="mono font-semibold">{order.buyer_details.gst_number}</p>
                                </div>
                              )}
                              <div>
                                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Delivery Location</p>
                                <p className="font-semibold">{order.delivery_location}</p>
                              </div>
                            </div>
                            <div className="mt-4 space-y-3">
                              <div>
                                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Billing Address</p>
                                <p className="text-sm bg-white p-2 border border-border rounded-none">{order.buyer_details.billing_address}</p>
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Delivery Address</p>
                                <p className="text-sm bg-white p-2 border border-border rounded-none">{order.buyer_details.delivery_address}</p>
                              </div>
                              {order.notes && (
                                <div>
                                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Order Notes</p>
                                  <p className="text-sm bg-white p-2 border border-border rounded-none italic">{order.notes}</p>
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          // Old orders - show basic info
                          <div className="space-y-3">
                            <div className="p-4 bg-yellow-50 border border-yellow-300 rounded-none">
                              <p className="text-sm font-semibold text-yellow-800 mb-2">⚠️ Limited Information Available</p>
                              <p className="text-xs text-yellow-700">This order was placed before buyer details collection was implemented. Only basic information is available.</p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Customer Name</p>
                                <p className="font-semibold">{order.user_name}</p>
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Email</p>
                                <p className="mono text-sm">{order.user_email}</p>
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Delivery Location</p>
                                <p className="font-semibold">{order.delivery_location}</p>
                              </div>
                            </div>
                            {order.notes && (
                              <div className="mt-3">
                                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Order Notes</p>
                                <p className="text-sm bg-white p-2 border border-border rounded-none italic">{order.notes}</p>
                              </div>
                            )}
                            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-none">
                              <p className="text-xs text-blue-700">💡 New orders will automatically capture complete buyer details including contact numbers, addresses, and GST information.</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between border-t border-border pt-4">
                    <span className={`px-3 py-1 border rounded-none text-xs uppercase tracking-wider font-bold ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                    <div className="flex gap-2">
                      {order.status === 'pending' && (
                        <Button
                          onClick={() => updateOrderStatus(order.id, 'confirmed')}
                          data-testid={`confirm-order-${order.id}`}
                          size="sm"
                          className="rounded-none uppercase tracking-wider text-xs"
                        >
                          CONFIRM
                        </Button>
                      )}
                      {order.status === 'confirmed' && (
                        <Button
                          onClick={() => updateOrderStatus(order.id, 'shipped')}
                          data-testid={`ship-order-${order.id}`}
                          size="sm"
                          className="rounded-none uppercase tracking-wider text-xs"
                        >
                          SHIP
                        </Button>
                      )}
                      {order.status === 'shipped' && (
                        <Button
                          onClick={() => updateOrderStatus(order.id, 'delivered')}
                          data-testid={`deliver-order-${order.id}`}
                          size="sm"
                          className="rounded-none uppercase tracking-wider text-xs"
                        >
                          DELIVER
                        </Button>
                      )}
                      {order.status === 'pending' && (
                        <Button
                          onClick={() => updateOrderStatus(order.id, 'cancelled')}
                          data-testid={`cancel-order-${order.id}`}
                          variant="outline"
                          size="sm"
                          className="rounded-none uppercase tracking-wider text-xs border-red-300 text-red-600 hover:bg-red-50"
                        >
                          CANCEL
                        </Button>
                      )}
                      {order.status === 'cancelled' && (
                        <Button
                          onClick={() => deleteOrder(order.id)}
                          data-testid={`delete-order-${order.id}`}
                          variant="destructive"
                          size="sm"
                          className="rounded-none uppercase tracking-wider text-xs"
                        >
                          DELETE
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
